// Create Lead → "Where is this patient up to?" (Raj, 24 Oct 2026).
//
// The two advanced answers ("already had their consultation", "already started
// treatment") reuse the ordinary Team + Appointment form: the date picker is
// the consultation date, the "Seen by (dentist)" chip is the practitioner.
// This module turns those form fields into the values the shared attendance
// validator (attendanceDecision) expects, so what gets SAVED is exactly what
// the dropdown version saved: one atomic create with an attendance decision.
import moment from 'moment';

import { attendanceDecision } from '../../../../components/attendance/attendanceCapture.ts';

export const CREATION_STAGES = Object.freeze([
    {
        value: 'NEW',
        title: 'New enquiry',
        hint: 'Not been in yet. You can book their consultation now or later.',
    },
    {
        value: 'CONSULTATION_ATTENDED',
        title: 'Already had their consultation',
        hint: 'They came in before this lead existed. Record when, and which dentist saw them.',
    },
    {
        value: 'TREATMENT_STARTED',
        title: 'Already started treatment',
        hint: 'Consultation done and treatment under way. Record the consultation, then we start them.',
    },
]);

export const isAdvancedStage = (stage) => stage === 'CONSULTATION_ATTENDED' || stage === 'TREATMENT_STARTED';

/** Wall-clock 'YYYY-MM-DDTHH:mm' the validator resolves in the practice time zone. */
export const localTimeOf = (date) => {
    if (!date) return '';
    const m = moment(date);
    return m.isValid() ? m.format('YYYY-MM-DDTHH:mm') : '';
};

/**
 * The validator's value object from the step's form fields.
 * @param {object} input  { date, practitionerId, noConsultation, reason }
 */
export const creationAttendanceValues = ({ date, practitionerId, noConsultation = false, reason = '' } = {}) => (
    noConsultation
        ? { kind: 'not_required', appointment: '', localTime: '', practitioner: '', reason: String(reason || ''), confirmed: false, offset: '' }
        : { kind: 'attended', appointment: 'new', localTime: localTimeOf(date), practitioner: practitionerId ? String(practitionerId) : '', reason: '', confirmed: false, offset: '' }
);

// Validator messages are written for the old field names; the step shows
// them under the control the user actually touched.
const FIELD_MESSAGES = {
    localTime: 'Enter when the consultation took place (it must be in the past).',
    offset: 'This time happened twice on clock-change day. Pick a time a few minutes either side.',
    practitioner: 'Choose the dentist who saw the patient.',
    reason: 'Say briefly why no consultation took place.',
    appointment: 'Choose a consultation date.',
    kind: 'This option is not available for this practice.',
};

/**
 * Build the capture the create thunk consumes, or the field errors to show.
 * @returns {{ capture: object } | { errors: Object<string,string> }}
 */
export const buildCreationCapture = (context, stage, input, { practiceId, operationKey, elapsedMs = 0 } = {}) => {
    if (!isAdvancedStage(stage)) return { capture: null };
    if (!context || context.target_status !== stage || String(context.practice_id) !== String(practiceId)) {
        return { errors: { context: 'Attendance options are still loading. Try again in a moment.' } };
    }
    if (stage === 'CONSULTATION_ATTENDED' && input?.noConsultation) {
        return { errors: { kind: 'A consultation is required to create the patient as attended.' } };
    }
    const values = creationAttendanceValues(input);
    const result = attendanceDecision(context, values, elapsedMs);
    if (!result.decision) {
        const errors = {};
        Object.keys(result.errors).forEach((key) => { errors[key] = FIELD_MESSAGES[key] || result.errors[key]; });
        return { errors };
    }
    return {
        capture: { practiceId: Number(practiceId), targetStatus: stage, operationKey, decision: result.decision, values },
    };
};
